import { IFinancialAccount } from '@/types'
import type { Order } from '../UI/DataTable'

export type AccountSortKey = keyof IFinancialAccount

// By nickname, A to Z, so an account is found by the name people know it by.
export const DEFAULT_ACCOUNT_SORT_BY: AccountSortKey = 'name'
export const DEFAULT_ACCOUNT_SORT_ORDER: Order = 'asc'

const AMOUNT_KEYS: AccountSortKey[] = ['availableFunds', 'balance']

// What a row is compared on, or undefined when it has nothing usable to compare.
const sortValue = (account: IFinancialAccount, sortBy: AccountSortKey) => {
  const value = account[sortBy]

  if (AMOUNT_KEYS.includes(sortBy)) {
    // The list swaps a missing value for a space, which Number() would read as zero.
    if (value == null || (typeof value === 'string' && value.trim() === '')) return undefined
    const amount = typeof value === 'number' ? value : Number(value)
    return Number.isNaN(amount) ? undefined : amount
  }

  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined
}

// Numeric, so "Account 2" comes before "Account 10" and masked numbers order by their digits.
const collator = new Intl.Collator('en-AU', { numeric: true, sensitivity: 'base' })

export const sortAccounts = (
  accounts: IFinancialAccount[],
  sortBy: AccountSortKey,
  order: Order
): IFinancialAccount[] => {
  const direction = order === 'asc' ? 1 : -1

  return accounts
    .map(account => ({ account, value: sortValue(account, sortBy) }))
    .sort((left, right) => {
      // An account with nothing to compare sinks to the bottom rather than scrambling the rest.
      if (left.value === undefined) return right.value === undefined ? 0 : 1
      if (right.value === undefined) return -1

      const compared =
        typeof left.value === 'number' && typeof right.value === 'number'
          ? left.value - right.value
          : collator.compare(String(left.value), String(right.value))
      return compared * direction
    })
    .map(({ account }) => account)
}
